import { useEffect, useId, useRef, useState } from "react";
import type { Dispatch, ReactElement, SetStateAction } from "react";

import { resolveCascade } from "./api";
import { compsFromCascade, type CascadeResolveArgs } from "./cascade";
import { normalizeAddress } from "./AddressMismatchPrompt";
import {
  DAYS_OPTIONS, TARGET_MIN_HOMES, hiddenListings, startingActives, wideningSteps,
  type ActivesCriteria, type ActiveStatus, type WideningStep,
} from "./activesFilter";
import { rankActives, type ActivesOrder, type RankSubject } from "./activesRankApi";
import type { CandidateComp } from "./types";

/** What this panel tells the listings table to paint: which rows, in what order, with what reason. */
export interface ActivesView {
  hidden: Set<number>;
  order: number[] | undefined;
  reasons: Record<number, string>;
}

const WHY_DEFAULT =
  "Best match is the same ranking the sold sales use: homes closest in size first, then nearer, " +
  "more recently listed, in the same subdivision and in similar condition. The date a home was listed " +
  "stands in for a sale date, since these homes have not sold. Nearest first sorts by distance alone.";

/**
 * FIND COMPETING HOMES — the competing-homes search, independent of the sold one.
 *
 * Radius, days listed, size, price band and status, starting at 1 mile, any days listed, ±400 sq ft
 * around this home's living area, any price, Active + Pending. Days, size, price and status filter
 * the listings already on screen instantly; the radius is the MLS listings search's own and applies
 * on Refresh, which asks the server again (an MLS read-only Search, through the cascade, listings
 * only: no provider lookup and no sold re-search). Without MLS for this account there is no listings
 * search to refresh, and the button says so in place.
 *
 * Order: Best match (the engine's ranking, asked of the server; the client never ranks) or Nearest
 * first. Fewer than five homes: the panel OFFERS stated widening steps, each one change, from and to.
 * It never widens by itself.
 *
 * Refresh replaces only the MLS listings; listings the agent's own file supplied are untouched, a
 * listing the agent ticked survives, and the sold sales are never read or written here.
 */
export function ActivesSearch({
  listings,
  onListingsChange,
  subjectGla,
  subject,
  appliedRadius,
  mlsAvailable,
  mlsReason,
  buildArgs,
  onView,
}: {
  listings: CandidateComp[];
  onListingsChange: Dispatch<SetStateAction<CandidateComp[]>>;
  subjectGla: number | null;
  /** The subject fields the ranking reads. */
  subject: RankSubject;
  /** The radius the listings now on screen were last searched to (null = not known). */
  appliedRadius: number | null;
  mlsAvailable: boolean;
  mlsReason?: string | null;
  buildArgs: () => CascadeResolveArgs;
  onView: (view: ActivesView) => void;
}): ReactElement {
  const [k, setK] = useState<ActivesCriteria>(() => startingActives());
  const [applied, setApplied] = useState<number | null>(appliedRadius);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<ActivesOrder>("best");
  const [whyOpen, setWhyOpen] = useState(false);
  const [rank, setRank] = useState<{ key: string; order: number[]; reasons: string[]; why: string } | null>(null);
  const [rankFailed, setRankFailed] = useState(false);
  const id = useId();

  const { hidden, keptOutside, unchecked } = hiddenListings(listings, k, subjectGla);
  const hiddenKey = [...hidden].join(",");
  const shownCount = listings.length - hidden.size;

  /* THE ORDER. Asked of the server over the rows now showing (so the reasons describe this set), a
     quarter second after the last change. A stale answer is dropped by its key. */
  const visible = listings.map((_, i) => i).filter((i) => !hidden.has(i));
  const askKey = JSON.stringify([orderBy, visible, subject, visible.map((i) => [listings[i].distance, listings[i].gla, listings[i].dom, listings[i].dom_as_of, listings[i].year, listings[i].subdivision])]);
  const seq = useRef(0);
  useEffect(() => {
    if (visible.length === 0) { setRank(null); setRankFailed(false); return; }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      rankActives(subject, visible.map((i) => listings[i]), orderBy)
        .then((r) => { if (seq.current === mine) { setRank({ key: askKey, order: r.order, reasons: r.reasons, why: r.why }); setRankFailed(false); } })
        .catch(() => { if (seq.current === mine) { setRank(null); setRankFailed(true); } });
    }, 250);
    return () => clearTimeout(t);
  }, [askKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = rank && rank.key === askKey ? rank : null;
  const view: ActivesView = (() => {
    if (!current) return { hidden, order: undefined, reasons: {} };
    const order = current.order.map((j) => visible[j]);
    const reasons: Record<number, string> = {};
    current.reasons.forEach((r, j) => { reasons[visible[j]] = r; });
    // Hidden rows are not painted; they go last so the order still covers every row.
    return { hidden, order: [...order, ...[...hidden]], reasons };
  })();
  const viewKey = `${hiddenKey}|${view.order?.join(",") ?? ""}|${Object.values(view.reasons).join("~")}`;
  useEffect(() => { onView(view); }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<ActivesCriteria>) => setK((cur) => ({ ...cur, ...patch }));
  const toggle = (s: ActiveStatus, on: boolean) =>
    set({ statuses: (["active", "pending"] as ActiveStatus[]).filter((x) => (x === s ? on : k.statuses.includes(x))) });
  const money = (v: string) => {
    const n = Number(v.replace(/[$,\s]/g, ""));
    return v.trim() === "" || !Number.isFinite(n) ? null : n;
  };

  async function refresh(next: ActivesCriteria = k) {
    if (!mlsAvailable || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { upload_pending: _u, file_comps: _f, agent_comps: _a, comp_params: _p, ...base } = buildArgs();
      const result = await resolveCascade({
        ...base, want_comps: true,
        // Refresh re-asks the listings search only. The server skips the provider lookup and the
        // sold re-search, so this button spends no lookup allowance.
        listings_only: true,
        // Sold is always part of a criteria set (the server keeps it on); the statuses here only
        // choose which listings are asked for. The sold rows that come back are ignored.
        comp_criteria: { statuses: ["sold", ...next.statuses], active_radius_mi: next.radius_mi },
      });
      const fresh = compsFromCascade(result.mls_actives ?? []);
      onListingsChange((ls) => {
        const mine = ls.filter((l) => l._source !== "NTREIS MLS" || l._selected === true);
        const have = new Set(mine.map((l) => normalizeAddress(l.name)));
        return [...mine, ...fresh.filter((l) => !have.has(normalizeAddress(l.name)))];
      });
      if (result.mls?.actives_searched) setApplied(next.radius_mi);
      setNote(result.mls?.actives_searched
        ? `Searched ${Number(next.radius_mi.toFixed(2))} mi for homes on the market: ${result.mls?.actives_found ?? fresh.length} found.`
        : (result.mls?.actives_reason || "The competing-homes search did not run."));
    } catch {
      setError("We couldn't reach the listings search just now. The homes already listed here are untouched. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function widen(step: WideningStep) {
    const next = { ...k, ...step.patch };
    setK(next);
    setNote(`Changed ${step.what.toLowerCase()} from ${step.from} to ${step.to}.`);
    if (step.needsRefresh && mlsAvailable) void refresh(next);
  }

  const radiusPending = applied === null || Math.abs(k.radius_mi - applied) > 1e-9;
  const steps = listings.length > 0 || applied !== null ? wideningSteps(k, subjectGla) : [];
  const offerWidening = shownCount < TARGET_MIN_HOMES && steps.length > 0;
  return (
    <section className="vb-as" aria-labelledby={`${id}-h`} data-testid="actives-search">
      <h3 id={`${id}-h`} className="vb-cs-h">Find competing homes</h3>
      <p className="vb-cs-why">
        Its own search, separate from the sold sales. Change anything here without touching the sales.
      </p>
      <div className="vb-cs-fields">
        <div className="vb-cs-field">
          <div className="vb-cs-field-row">
            <label htmlFor={`${id}-r`}>Radius for homes on the market</label>
            <output htmlFor={`${id}-r`} aria-hidden="true">{Number(k.radius_mi.toFixed(2))} mi</output>
          </div>
          <input id={`${id}-r`} type="range" min={0.25} max={5} step={0.25} value={k.radius_mi}
                 aria-valuetext={`${Number(k.radius_mi.toFixed(2))} miles`}
                 onChange={(e) => set({ radius_mi: Number(e.target.value) })} />
          {radiusPending && (
            <p className="vb-cs-why" data-testid="actives-radius-pending">
              {!mlsAvailable
                ? "A new radius can't be searched here: no listings search is available for this account."
                : applied === null
                  ? `The homes below were not searched to ${Number(k.radius_mi.toFixed(2))} mi. Refresh to search that far.`
                  : `The homes below were searched to ${Number(applied.toFixed(2))} mi. A new radius applies when you refresh.`}
            </p>
          )}
        </div>
        <div className="vb-cs-field">
          <label htmlFor={`${id}-d`}>Days listed</label>
          <select id={`${id}-d`} value={k.max_days ?? ""} onChange={(e) => set({ max_days: e.target.value === "" ? null : Number(e.target.value) })}>
            <option value="">Any</option>
            {DAYS_OPTIONS.map((d) => <option key={d} value={d}>{d} days or fewer</option>)}
          </select>
        </div>
        <div className="vb-cs-field">
          <label htmlFor={`${id}-g`}>Size</label>
          {subjectGla ? (
            <select id={`${id}-g`} value={k.gla_band_sqft ?? ""} onChange={(e) => set({ gla_band_sqft: e.target.value === "" ? null : Number(e.target.value) })}>
              {[200, 300, 400, 500, 600, 750, 800, 1000].map((n) => (
                <option key={n} value={n}>within ±{n.toLocaleString("en-US")} sq ft of this home</option>
              ))}
              {k.gla_band_sqft !== null && ![200, 300, 400, 500, 600, 750, 800, 1000].includes(k.gla_band_sqft) && (
                <option value={k.gla_band_sqft}>within ±{k.gla_band_sqft.toLocaleString("en-US")} sq ft of this home</option>
              )}
              <option value="">Any size</option>
            </select>
          ) : (
            <>
              <select id={`${id}-g`} disabled value="unknown" aria-describedby={`${id}-g-why`}>
                <option value="unknown">Living area unknown — size filter can&apos;t apply</option>
              </select>
              <p id={`${id}-g-why`} className="vb-cs-why">This home&apos;s living area isn&apos;t on record yet.</p>
            </>
          )}
        </div>
        <div className="vb-cs-field vb-cs-field-pair">
          <div>
            <label htmlFor={`${id}-pmin`}>Asking price from</label>
            <input id={`${id}-pmin`} inputMode="numeric" placeholder="Any" value={k.price_min ?? ""}
                   onChange={(e) => set({ price_min: money(e.target.value) })} />
          </div>
          <div>
            <label htmlFor={`${id}-pmax`}>to</label>
            <input id={`${id}-pmax`} inputMode="numeric" placeholder="Any" value={k.price_max ?? ""}
                   onChange={(e) => set({ price_max: money(e.target.value) })} />
          </div>
        </div>
        <fieldset className="vb-cs-field vb-cs-status-set">
          <legend>Status</legend>
          {(["active", "pending"] as ActiveStatus[]).map((s) => (
            <label key={s} className="vb-cs-check">
              <input type="checkbox" checked={k.statuses.includes(s)} onChange={(e) => toggle(s, e.target.checked)} />
              <span>{s === "active" ? "Active" : "Pending"}</span>
            </label>
          ))}
        </fieldset>
        <div className="vb-cs-field">
          <div className="vb-cs-field-row">
            <label htmlFor={`${id}-o`}>Order by</label>
            <button type="button" className="vb-as-why-btn" aria-expanded={whyOpen} aria-controls={`${id}-why`}
                    aria-label="Why this order" title="Why this order" onClick={() => setWhyOpen((o) => !o)}>(i)</button>
          </div>
          <select id={`${id}-o`} value={orderBy} onChange={(e) => setOrderBy(e.target.value as ActivesOrder)}>
            <option value="best">Best match</option>
            <option value="nearest">Nearest first</option>
          </select>
          {whyOpen && <p id={`${id}-why`} className="vb-cs-why" data-testid="actives-why">{current?.why ?? WHY_DEFAULT}</p>}
        </div>
      </div>
      <div className="vb-cs-actions">
        <button type="button" className="vb-cs-btn vb-cs-btn-search" disabled={!mlsAvailable || busy}
                aria-describedby={!mlsAvailable ? `${id}-nomls` : undefined} aria-busy={busy || undefined}
                onClick={() => void refresh()}>
          {busy ? "Searching…" : "Refresh"}
        </button>
        {!mlsAvailable && (
          <span id={`${id}-nomls`} className="vb-cs-note">
            {mlsReason || "Refresh searches MLS listings, and MLS isn't turned on for this account."} The filters still apply to the homes listed below.
          </span>
        )}
      </div>
      <div role="status" aria-live="polite">
        {note && <p className="vb-cs-note">{note}</p>}
        {listings.length > 0 && (
          <p className="vb-cs-note" data-testid="actives-shown">
            Showing {shownCount} of {listings.length} {listings.length === 1 ? "home" : "homes"} on the market.
            {keptOutside > 0 ? ` ${keptOutside} you chose for the report ${keptOutside === 1 ? "is" : "are"} outside these filters and still shown.` : ""}
            {unchecked > 0 ? ` ${unchecked} ${unchecked === 1 ? "listing doesn't" : "listings don't"} state every field filtered on; ${unchecked === 1 ? "it is" : "they are"} kept, not ruled out.` : ""}
          </p>
        )}
        {rankFailed && (
          <p className="vb-cs-note" data-testid="actives-rank-failed">
            We couldn&apos;t order these homes just now, so they are shown in the order they came in.
          </p>
        )}
      </div>
      {offerWidening && (
        <div className="vb-as-widen" data-testid="actives-widen">
          <p className="vb-cs-note">
            {shownCount === 0 ? "No homes match." : `Only ${shownCount} ${shownCount === 1 ? "home matches" : "homes match"}.`}
            {" "}You can widen the search, one step at a time:
          </p>
          <ul className="vb-as-widen-list">
            {steps.map((s) => (
              <li key={s.id}>
                <button type="button" className="vb-cs-btn" disabled={busy}
                        onClick={() => widen(s)}>
                  {s.what}: {s.from} to {s.to}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="vb-cs-error" role="alert">{error}</p>}
    </section>
  );
}
